import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useLocaleStore } from '../stores/locale'
import { useOnboardingStore } from '../stores/onboarding'
import { useSectionStore } from '../stores/sections'
import { i18nGlobal, isSupportedLocale, resolveInitialLocale, type AppLocale } from '../i18n'

// Publieke (crawlbare) pagina's dragen de taalcode in de URL — /en/… en
// /nl/… — zodat elke taal een eigen, deelbare en indexeerbare URL heeft
// (met hreflang). De ingelogde app doet dit bewust NIET: die volgt de
// voorkeur van de gebruiker op één set URL's (geen SEO-belang, wel veel
// minder complexiteit). `pageKey` koppelt de twee taalvarianten zodat de
// taalswitcher naar dezelfde pagina in de andere taal kan navigeren.
export type PublicPageKey = 'landing' | 'terms' | 'privacy' | 'disclaimer'
const PUBLIC_LOCALE_PATHS: Record<PublicPageKey, Record<AppLocale, string>> = {
  landing: { en: '/en', nl: '/nl' },
  terms: { en: '/en/terms', nl: '/nl/voorwaarden' },
  privacy: { en: '/en/privacy', nl: '/nl/privacy' },
  disclaimer: { en: '/en/disclaimer', nl: '/nl/disclaimer' },
}

/** URL van een publieke pagina in een specifieke taal (voor de switcher). */
export function publicLocalePath(page: PublicPageKey, locale: AppLocale): string {
  return PUBLIC_LOCALE_PATHS[page][locale]
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  
  // Scroll behavior - return to top on navigation
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // Focus main content for accessibility
    return { top: 0 }
  },
  
  routes: [
    {
      // De startpagina rendert de aangewezen homepage (of een fallback).
      // Op het kale platformdomein (pentrical.com, VITE_PLATFORM_HOST) is
      // er geen tenant en tonen we de marketing-landingspagina.
      path: '/',
      name: 'home',
      component: () => import('../views/pages/PageView.vue'),
      meta: { titleKey: 'routeTitles.home', docKind: 'home' },
      beforeEnter: () => {
        const platformHost = import.meta.env.VITE_PLATFORM_HOST as string | undefined
        if (platformHost && window.location.host.replace(/^www\./, '') === platformHost) {
          /*
           * Het app-domein (app.pentrical.com): hier staat geen documentatie
           * en geen marketing — de website heeft het kale domein, de
           * documentatie staat op het adres van de omgeving zelf. Wat
           * overblijft is de doorgang: naar je eigen omgeving als we weten
           * wie je bent, anders naar het inlogscherm.
           *
           * Ingelogd zijn kán hier normaal gesproken niet: de centrale login
           * bewaart met opzet geen token op dit domein maar wisselt een
           * eenmalige code in voor een sessie op je eigen omgeving (zie
           * stores/auth.ts). Een sessie die over alle subdomeinen geldt zou
           * betekenen dat elke klantomgeving erbij kan, en dat is precies
           * wat deze opzet vermijdt.
           *
           * De check staat er toch, want in dev valt het platformdomein
           * samen met het fallback-account en is er wél een sessie. Dan hoor
           * je niet naar een inlogscherm gestuurd te worden.
           */
          return useAuthStore().isAuthenticated ? true : '/login'
        }
        return true
      }
    },
    // ---- Publieke marketing/juridische pagina's (taalcode in de URL) ----
    // Engels op /en/…, Nederlands op /nl/…; meta.locale dwingt de taal af
    // (los van de voorkeur, voor SEO en deelbare links), meta.pageKey
    // koppelt beide taalvarianten voor de switcher.
    {
      /*
       * De marketing-landingspagina is verhuisd naar de server-side website
       * op het kale domein (pentrical.com); de Vue-versie hieronder is de
       * oude kopie en loopt inhoudelijk achter (geen Team-plan). Zodra we
       * weten waar de echte website woont, sturen we bezoekers daarheen in
       * plaats van twee verschillende prijslijsten naast elkaar te tonen.
       * Het websiteadres is het platformdomein zonder het app-voorvoegsel
       * (app.staging.pentrical.com → staging.pentrical.com). Alleen in dev,
       * zonder VITE_PLATFORM_HOST, blijft de oude pagina zichtbaar.
       */
      path: '/en',
      name: 'landing',
      component: () => import('../views/marketing/LandingView.vue'),
      meta: { titleKey: 'routeTitles.landing', locale: 'en', pageKey: 'landing' },
      beforeEnter: () => {
        const platformHost = import.meta.env.VITE_PLATFORM_HOST as string | undefined
        if (platformHost) {
          window.location.replace(`https://${platformHost.replace(/^app\./, '')}`)
          return false
        }
        return true
      }
    },
    {
      path: '/nl',
      name: 'landing-nl',
      component: () => import('../views/marketing/LandingView.vue'),
      meta: { titleKey: 'routeTitles.landing', locale: 'nl', pageKey: 'landing' },
      beforeEnter: () => {
        const platformHost = import.meta.env.VITE_PLATFORM_HOST as string | undefined
        if (platformHost) {
          window.location.replace(`https://${platformHost.replace(/^app\./, '')}/nl`)
          return false
        }
        return true
      }
    },
    {
      path: '/en/terms',
      name: 'terms',
      component: () => import('../views/marketing/LegalView.vue'),
      meta: { titleKey: 'routeTitles.terms', locale: 'en', pageKey: 'terms', doc: 'terms' }
    },
    {
      path: '/nl/voorwaarden',
      name: 'terms-nl',
      component: () => import('../views/marketing/LegalView.vue'),
      meta: { titleKey: 'routeTitles.terms', locale: 'nl', pageKey: 'terms', doc: 'terms' }
    },
    {
      path: '/en/privacy',
      name: 'privacy',
      component: () => import('../views/marketing/LegalView.vue'),
      meta: { titleKey: 'routeTitles.privacy', locale: 'en', pageKey: 'privacy', doc: 'privacy' }
    },
    {
      path: '/nl/privacy',
      name: 'privacy-nl',
      component: () => import('../views/marketing/LegalView.vue'),
      meta: { titleKey: 'routeTitles.privacy', locale: 'nl', pageKey: 'privacy', doc: 'privacy' }
    },
    {
      path: '/en/disclaimer',
      name: 'disclaimer',
      component: () => import('../views/marketing/LegalView.vue'),
      meta: { titleKey: 'routeTitles.disclaimer', locale: 'en', pageKey: 'disclaimer', doc: 'disclaimer' }
    },
    {
      path: '/nl/disclaimer',
      name: 'disclaimer-nl',
      component: () => import('../views/marketing/LegalView.vue'),
      meta: { titleKey: 'routeTitles.disclaimer', locale: 'nl', pageKey: 'disclaimer', doc: 'disclaimer' }
    },
    // Oude paden (vóór de taalprefix) → 301-achtige redirects naar de
    // nieuwe canonieke URL's, zodat bestaande links blijven werken.
    { path: '/landing', redirect: '/en' },
    { path: '/voorwaarden', redirect: '/nl/voorwaarden' },
    { path: '/privacy', redirect: '/nl/privacy' },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/auth/LoginView.vue'),
      meta: { titleKey: 'routeTitles.login', guest: true }
    },
    {
      // Wachtwoord vergeten: resetlink aanvragen.
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../views/auth/ForgotPasswordView.vue'),
      meta: { titleKey: 'routeTitles.forgotPassword' }
    },
    {
      // Nieuw wachtwoord instellen via de link uit de mail (token+email in query).
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../views/auth/ResetPasswordView.vue'),
      meta: { titleKey: 'routeTitles.resetPassword' }
    },
    {
      // Ontvangst van een centrale login: wissel de eenmalige code (query)
      // in voor een sessie op dit subdomein. Geen guest-vlag — het is een
      // transiënt scherm dat zichzelf naar de app doorstuurt.
      path: '/auth/handoff',
      name: 'handoff',
      component: () => import('../views/auth/HandoffView.vue'),
      meta: { titleKey: 'routeTitles.handoff' }
    },
    {
      // Zelfregistratie (SAAS_PLAN.md §6): nieuw account + eigen omgeving.
      // Bewust GEEN guest-vlag: de wizard maakt een níéuw account aan en
      // moet dus ook bereikbaar zijn vanaf de landingspagina voor wie al
      // ergens ingelogd is (anders eindigt "Gratis proberen" in de app).
      path: '/signup',
      name: 'signup',
      component: () => import('../views/auth/SignupView.vue'),
      meta: { titleKey: 'routeTitles.signup' }
    },
    {
      // De betaalpagina op het hoofddomein: Paddle opent zijn venster alleen
      // op een goedgekeurd adres, dus elke omgeving rekent hier af (zie de
      // toelichting in CheckoutView). Publiek: hier is niemand ingelogd.
      path: '/checkout',
      name: 'checkout',
      component: () => import('../views/account/CheckoutView.vue'),
      meta: { titleKey: 'routeTitles.checkout' }
    },
    {
      // Teamuitnodiging accepteren (SAAS_PLAN.md §2): losstaand
      // fullscreen-scherm, publiek — de ontvanger heeft nog geen login.
      path: '/invitation/:token',
      name: 'invitation-accept',
      component: () => import('../views/auth/AcceptInvitationView.vue'),
      meta: { titleKey: 'routeTitles.invitation' }
    },
    {
      // Aan de slag: de stappenlijst + uitleg voor redacteuren. Dit is ook
      // wat een redacteur op / ziet zolang er geen homepage is ingesteld
      // (PageView rendert het scherm daar ingebed); dit adres houdt het
      // bereikbaar nádat er een homepage is. 'start' is als hub-slug
      // gereserveerd (HubController::RESERVED_SLUGS).
      path: '/start',
      name: 'getting-started',
      component: () => import('../views/GettingStartedView.vue'),
      meta: { titleKey: 'routeTitles.gettingStarted', requiresAuth: true }
    },
    {
      path: '/pages',
      name: 'pages',
      component: () => import('../views/pages/PagesView.vue'),
      meta: { titleKey: 'routeTitles.pages', requiresAuth: true }
    },
    {
      path: '/pages/:id',
      name: 'page',
      component: () => import('../views/pages/PageView.vue'),
      meta: { titleKey: 'routeTitles.page', requiresAuth: true }
    },
    {
      // Publieke documentatie-pagina (bezoekersweergave, geen auth)
      path: '/docs/:sectionSlug/:pageSlug',
      name: 'public-page',
      component: () => import('../views/pages/PageView.vue'),
      meta: { titleKey: 'routeTitles.docs', docKind: 'page' }
    },
    {
      // Publieke tab (kindpagina) van een documentatie-pagina
      path: '/docs/:sectionSlug/:pageSlug/:tabSlug',
      name: 'public-tab',
      component: () => import('../views/pages/PageView.vue'),
      meta: { titleKey: 'routeTitles.docs', docKind: 'tab' }
    },
    {
      path: '/pages/:id/edit',
      name: 'page-editor',
      component: () => import('../views/pages/PageEditorView.vue'),
      meta: { titleKey: 'routeTitles.pageEditor', requiresAuth: true }
    },
    {
      // Settings-scherm: linkermenu + geneste onderdelen.
      // requiresAuth op de parent dekt alle children (meta wordt gemerged).
      path: '/settings',
      component: () => import('../views/settings/SettingsLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        { path: '', redirect: { name: 'figma-library' } },
        {
          path: 'figma',
          // Naam behouden: FigmaFileView linkt terug via deze naam.
          name: 'figma-library',
          component: () => import('../views/figma/FigmaLibraryView.vue'),
          meta: { titleKey: 'routeTitles.figmaLibrary' }
        },
        {
          path: 'tokens',
          name: 'tokens',
          component: () => import('../views/tokens/DesignTokensView.vue'),
          meta: { titleKey: 'routeTitles.tokens' }
        },
        {
          path: 'thema',
          name: 'settings-theme',
          component: () => import('../views/settings/ThemeSettingsView.vue'),
          meta: { titleKey: 'routeTitles.theme' }
        },
        {
          // Wie de documentatie mag lezen (sitebreed slot + categorie-sloten).
          path: 'access',
          name: 'settings-access',
          component: () => import('../views/settings/AccessSettingsView.vue'),
          meta: { titleKey: 'routeTitles.access' }
        },
        {
          // De MCP-server: koppeling voor AI-tools.
          path: 'mcp',
          name: 'settings-mcp',
          component: () => import('../views/settings/McpServerView.vue'),
          meta: { titleKey: 'routeTitles.mcpServer' }
        },
        {
          // Waar de gepubliceerde Storybook staat; de Storybook-blokken op
          // pagina's leiden hun adres hiervan af.
          path: 'storybook',
          name: 'settings-storybook',
          component: () => import('../views/settings/StorybookSettingsView.vue'),
          meta: { titleKey: 'routeTitles.storybook' }
        },
        {
          // Analytics van deze hub (§14g). Team en hoger; het scherm
          // legt zelf uit wat het is als het plan er geen recht op geeft.
          path: 'analytics',
          name: 'settings-insights',
          component: () => import('../views/settings/InsightsSettingsView.vue'),
          meta: { titleKey: 'routeTitles.insights' }
        },
      ]
    },
    {
      // Account-scherm: persoonlijke voorkeuren, bereikbaar via het
      // gebruikersmenu. requiresAuth op de parent dekt alle children.
      path: '/account',
      component: () => import('../views/account/AccountLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        { path: '', redirect: { name: 'account-profile' } },
        {
          path: 'profiel',
          name: 'account-profile',
          component: () => import('../views/account/ProfileSettingsView.vue'),
          meta: { titleKey: 'routeTitles.profile' }
        },
        {
          path: 'team',
          name: 'account-team',
          component: () => import('../views/account/TeamSettingsView.vue'),
          meta: { titleKey: 'routeTitles.team' }
        },
        {
          path: 'hubs',
          name: 'account-hubs',
          component: () => import('../views/account/HubsSettingsView.vue'),
          meta: { titleKey: 'routeTitles.hubs' }
        },
        {
          /* Het pad blijft 'notificaties': het scherm heet nu Communicatie,
             maar een adres omgooien breekt opgeslagen links zonder dat er
             iets voor terugkomt. */
          path: 'notificaties',
          name: 'account-communication',
          component: () => import('../views/account/CommunicationSettingsView.vue'),
          meta: { titleKey: 'routeTitles.communication' }
        },
        {
          path: 'toegankelijkheid',
          name: 'account-accessibility',
          component: () => import('../views/account/AccessibilitySettingsView.vue'),
          meta: { titleKey: 'routeTitles.accessibility' }
        },
        {
          path: 'facturering',
          name: 'account-billing',
          component: () => import('../views/account/BillingSettingsView.vue'),
          meta: { titleKey: 'routeTitles.billing' }
        },
        {
          path: 'over',
          name: 'account-about',
          component: () => import('../views/account/AboutView.vue'),
          meta: { titleKey: 'routeTitles.about' }
        },
      ]
    },
    // Oude bladwijzers/deep links blijven werken:
    { path: '/figma', redirect: '/settings/figma' },
    { path: '/tokens', redirect: '/settings/tokens' },
    // De MCP-koppeling stond eerst onder Mijn account; het toestemmingsscherm
    // en eerdere instructies verwijzen nog naar dat adres.
    { path: '/account/ai-koppeling', redirect: '/settings/mcp' },
    { path: '/settings/profiel', redirect: '/account/profiel' },
    { path: '/settings/notificaties', redirect: '/account/notificaties' },
    { path: '/settings/toegankelijkheid', redirect: '/account/toegankelijkheid' },
    {
      // Detailpagina blijft standalone (geen padconflict met de redirect).
      path: '/figma/:id',
      name: 'figma-file',
      component: () => import('../views/figma/FigmaFileView.vue'),
      meta: { titleKey: 'routeTitles.figmaFile', requiresAuth: true }
    },
    {
      path: '/figma/:id/viewer/:frameId?',
      name: 'figma-viewer',
      component: () => import('../views/figma/FigmaViewerView.vue'),
      // Bewust géén requiresAuth (SAAS_PLAN.md §15h): wie op een ingesloten
      // ontwerp klikt, hoort het te kunnen bekijken zonder account. Het scherm
      // vraagt de server om dat ene ontwerp, en de server beslist of deze
      // bezoeker de pagina mag zien waar het op staat - dus openstellen
      // verbreedt hier niets aan toegang, het haalt alleen een inlogscherm weg
      // dat voor een bezoeker een doodlopende weg was.
      meta: { titleKey: 'routeTitles.figmaViewer' }
    },
    {
      path: '/accessibility',
      name: 'accessibility',
      component: () => import('../views/AccessibilityStatementView.vue'),
      meta: { titleKey: 'routeTitles.accessibilityStatement' }
    },
    /*
     * Documentatie van een níét-primaire hub (§14). De primaire hub staat
     * op de kale paden hierboven, zodat gedeelde links blijven werken;
     * elke andere hub komt achter zijn slug te staan.
     *
     * Deze routes staan met opzet onderaan, vlak vóór de 404: ze beginnen
     * met een vrije parameter en zouden anders `/settings`, `/cms` en
     * consorten opslokken. De server weigert bovendien slugs die met die
     * paden botsen (HubController::RESERVED_SLUGS), dus het is aan beide
     * kanten afgedekt.
     */
    {
      /*
       * Dit subdomein hoort bij geen enkele klant. De API-interceptor stuurt
       * hierheen zodra de server `unknown_environment` meldt.
       *
       * Staat bewust vóór de hub-routes hieronder: die beginnen met een vrije
       * parameter en zouden dit pad anders opslokken. De server weigert de
       * slug ook als hubnaam (HubController::RESERVED_SLUGS), dus het is aan
       * beide kanten afgedekt.
       */
      path: '/unknown-environment',
      name: 'unknown-environment',
      component: () => import('../views/UnknownEnvironmentView.vue'),
      meta: { titleKey: 'unknownEnvironment.title' }
    },
    {
      path: '/:hubSlug/docs/:sectionSlug/:pageSlug',
      name: 'hub-public-page',
      component: () => import('../views/pages/PageView.vue'),
      meta: { titleKey: 'routeTitles.docs', docKind: 'page' }
    },
    {
      path: '/:hubSlug/docs/:sectionSlug/:pageSlug/:tabSlug',
      name: 'hub-public-tab',
      component: () => import('../views/pages/PageView.vue'),
      meta: { titleKey: 'routeTitles.docs', docKind: 'tab' }
    },
    {
      path: '/:hubSlug',
      name: 'hub-home',
      component: () => import('../views/HomeView.vue'),
      meta: { titleKey: 'routeTitles.home', docKind: 'home' }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue'),
      meta: { titleKey: 'routeTitles.notFound' }
    }
  ]
})

// Navigation guard for authentication
/*
 * Oude adressen met de hub in het pad sturen door naar het eigen adres van
 * die hub (SAAS_PLAN.md §15, fase 2).
 *
 * `/tweede-merk/docs/kleur/primair` op het adres van een ándere hub liet
 * vroeger de inhoud van tweede-merk zien, doordat de slug als header werd
 * meegestuurd. Nu bepaalt de host welke hub het is, dus zo'n link zou op de
 * verkeerde hub uitkomen en een 404 geven. Doorsturen houdt gedeelde links
 * heel.
 *
 * **Alleen voor de twee docs-routes.** De route `/:hubSlug` is een vangroute
 * die élk niet-herkend pad van één segment opvangt: `/kleuren` zou daar ook
 * in vallen, en dan stuurden we een bezoeker naar `kleuren.pentrical.com` —
 * een adres dat niet bestaat. Bij `/x/docs/y/z` is het onmiskenbaar een hub.
 */
const HUB_PAD_ROUTES = new Set(['hub-public-page', 'hub-public-tab'])

router.beforeEach((to, from, next) => {
  const hubInPad = typeof to.params.hubSlug === 'string' ? to.params.hubSlug : ''
  const host = window.location.host

  if (hubInPad === '' || !HUB_PAD_ROUTES.has(String(to.name)) || !host.includes('.')) {
    return next()
  }

  // Het eerste label van de host vervangen door de hub uit het pad, en dat
  // pad-voorvoegsel eraf: op zijn eigen adres heet de pagina gewoon /docs/...
  const doelHost = [hubInPad, ...host.split('.').slice(1)].join('.')
  const restPad = to.fullPath.slice(hubInPad.length + 1) || '/'

  if (doelHost === host) {
    // Al op het goede adres, alleen het voorvoegsel is overbodig.
    return next(restPad)
  }

  window.location.replace(`${window.location.protocol}//${doelHost}${restPad}`)
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const localeStore = useLocaleStore()


  // Taal per route: marketingpagina's dwingen hun eigen taal af (voor SEO
  // en directe links), overige routes volgen de opgeslagen voorkeur.
  if (isSupportedLocale(to.meta.locale)) {
    localeStore.setLocale(to.meta.locale, false)
  } else {
    localeStore.restorePreferred()
  }

  // Update document title for accessibility. `titleKey` wordt vertaald;
  // `title` is de (nog niet gemigreerde) letterlijke fallback.
  const baseTitle = 'Pentrical'
  const pageTitle = to.meta.titleKey
    ? i18nGlobal.t(to.meta.titleKey as string)
    : (to.meta.title as string | undefined)
  document.title = pageTitle ? `${pageTitle} | ${baseTitle}` : baseTitle

  // Sessie herstellen bij volledige page-load: er is een token maar de
  // gebruiker is nog niet opgehaald. Zonder deze await verliest de guard
  // de race en wordt een ingelogde gebruiker naar /login gestuurd.
  if (authStore.token && !authStore.user) {
    await authStore.fetchUser()
  }

  /*
   * Een uitgelogde bezoeker op een intern pagina-adres (/pages/3) sturen we
   * door naar het publieke adres van diezelfde pagina.
   *
   * Zo'n adres belandt makkelijk buiten de app: het staat in de adresbalk
   * van elke beheerder, dus het wordt gekopieerd in card-links, gedeeld in
   * berichten en gebookmarkt. Zonder deze stap krijgt de ontvanger een
   * inlogscherm, terwijl de pagina gewoon openbaar is.
   *
   * Het nummer is bovendien duurzamer dan het pad: hernoem of verplaats een
   * pagina en /pages/3 wijst nog steeds goed, terwijl een opgeslagen
   * /docs/categorie/pagina stilletjes breekt.
   *
   * De bezoekersnavigatie is de bron, en die bevat per definitie alleen wat
   * deze bezoeker mag zien. Staat de pagina er niet in (concept, verborgen,
   * of een categorie die volledig verborgen is), dan gebeurt er niets en
   * volgt gewoon het inlogscherm - doorsturen zou anders verraden dat die
   * pagina bestaat.
   */
  if (to.name === 'page' && !authStore.token) {
    const sectionStore = useSectionStore()
    const id = Number(to.params.id)

    if (!Number.isNaN(id)) {
      await sectionStore.fetchTree(true)
      const publiek = sectionStore.publicPathForPage(id)

      if (publiek) {
        next(publiek)
        return
      }
    }
  }

  /*
   * Naar het inlogscherm alleen wanneer er géén token is.
   *
   * Eerder stond hier isAuthenticated (token én opgehaalde gebruiker). Kon
   * de gebruiker even niet opgehaald worden - rate limit, serverhapering,
   * korte netwerkonderbreking - dan werd je weggestuurd terwijl je token
   * gewoon geldig was. Het token ís de sessie; de gebruikersgegevens zijn
   * profielinformatie die de volgende navigatie opnieuw probeert.
   *
   * Blijkt het token tóch ongeldig, dan levert het eerstvolgende verzoek
   * een 401 en ruimt de API-client hem alsnog op (zie api/client.ts).
   */
  if (to.meta.requiresAuth && !authStore.token) {
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }
  
  // Redirect logged-in users away from guest-only pages
  if (to.meta.guest && authStore.isAuthenticated) {
    next({ name: 'home' })
    return
  }
  
  next()
})

// Announce route changes to screen readers
router.afterEach((to) => {
  // Use a small delay to ensure DOM is updated
  setTimeout(() => {
    const announcer = document.getElementById('route-announcer')
    if (announcer) {
      const title = to.meta.titleKey
        ? i18nGlobal.t(to.meta.titleKey as string)
        : (to.meta.title as string | undefined) || i18nGlobal.t('a11y.routeAnnounceFallback')
      announcer.textContent = i18nGlobal.t('a11y.routeAnnounce', { title })
    }
  }, 100)
})

/*
 * De Aan-de-slag-voortgang opnieuw meten bij elke navigatie, zolang er
 * nog stappen openstaan. Een stap doe je elders (pagina maken, team
 * uitnodigen) en het resultaat zie je in de chip en op het startscherm -
 * zonder dit ververste dat pas bij een volledige paginaherlaad. Gedempt
 * tot eens per vijf seconden, en zodra alles af is stopt het vanzelf.
 */
let laatsteOnboardingMeting = 0
router.afterEach(() => {
  const auth = useAuthStore()
  if (!auth.isAuthenticated || !auth.isEditor) return

  const onboarding = useOnboardingStore()
  if (!onboarding.loaded || onboarding.allDone) return

  const nu = Date.now()
  if (nu - laatsteOnboardingMeting < 5000) return
  laatsteOnboardingMeting = nu

  onboarding.fetchSteps(true)
})

export default router
